import onAttachmentCreated from './actions/onAttachmentCreated';
import onAttachmentDeleted from './actions/onAttachmentDeleted';
import AttachmentWellViewState from './schema/AttachmentWellViewState';
import ConversationAttachmentWellViewState from './schema/ConversationAttachmentWellViewState';
import AttachmentPreviewMethod from 'owa-attachment-data/lib/schema/AttachmentPreviewMethod';
import AttachmentViewState from 'owa-attachment-data/lib/schema/AttachmentViewState';
import ImageLoadState from 'owa-attachment-data/lib/schema/ImageLoadState';
import AttachmentFullViewState from 'owa-attachment-full-data/lib/schema/AttachmentFullViewState';
import AttachmentFullViewStrategy from 'owa-attachment-full-data/lib/schema/AttachmentFullViewStrategy';
import AttachmentMenuAction from 'owa-attachment-full-data/lib/schema/AttachmentMenuAction';
import AttachmentState from 'owa-attachment-full-data/lib/schema/AttachmentState';
import AttachmentStateType from 'owa-attachment-full-data/lib/schema/AttachmentStateType';
import InlineAttachmentState from 'owa-attachment-full-data/lib/schema/InlineAttachmentState';
import InlineAttachmentStatus from 'owa-attachment-full-data/lib/schema/InlineAttachmentStatus';
import { AttachmentModelPropertyValues } from 'owa-attachment-model-store';
import { LazyAction, LazyImport, LazyModule } from 'owa-bundling';
import { ClientAttachmentId, ClientItemId, MailboxInfo } from 'owa-client-ids';
import { InfoBarMessageViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import AttachmentDataProviderItem from 'owa-service/lib/contract/AttachmentDataProviderItem';
import AttachmentDataProviderItemType from 'owa-service/lib/contract/AttachmentDataProviderItemType';
import AttachmentId from 'owa-service/lib/contract/AttachmentId';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ItemId from 'owa-service/lib/contract/ItemId';
import WacAttachmentType from 'owa-service/lib/contract/WacAttachmentType';
import {
    CreateAttachmentsProperties,
    ShareAttachmentProperties,
    ShouldShareAttachment,
} from './types/CreateAttachmentsProperties';
import {
    CreateAttachmentDependencies,
    CreateAttachmentProcessResult,
} from './actions/createAttachment';
import {
    AttachmentFile,
    AttachmentFileList,
    CloudFile,
    CloudFileList,
} from 'owa-attachment-file-types';
import {
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
} from './types/AttachmentUploadQueueItem';

const lazyModule = new LazyModule(() =>
    import(/* webpackChunkName: "Attachments", webpackPrefetch: true */ './lazyIndex')
);

// Delayed Loaded Actions
export const lazyInitializeAttachments = new LazyAction(lazyModule, m => m.initializeAttachments);

export const lazyReinitializeAttachments = new LazyAction(
    lazyModule,
    m => m.reinitializeAttachments
);

export const lazySetInlineAttachmentStatus = new LazyAction(
    lazyModule,
    m => m.setInlineAttachmentStatus
);

export const lazyAddAttachmentIntoWellBeforeUpload = new LazyAction(
    lazyModule,
    m => m.addAttachmentIntoWellBeforeUpload
);

export const lazyRefreshSharingTipsAttachmentWell = new LazyImport(
    lazyModule,
    m => m.refreshSharingTipsAttachmentWell
);

// Delay Loaded utils
export const lazyCreateAttachmentsViaQueueManager = new LazyImport(
    lazyModule,
    m => m.createAttachmentsViaQueueManager
);

export const lazyDeleteAttachmentsViaQueueManager = new LazyAction(
    lazyModule,
    m => m.deleteAttachmentViaQueueManager
);

export const lazyCreateAttachmentFullViewStateAndStoreBackingModel = new LazyImport(
    lazyModule,
    m => m.createAttachmentFullViewStateAndStoreBackingModel
);

export const lazyCreateAttachmentViewState = new LazyImport(
    lazyModule,
    m => m.createAttachmentFullViewState
);

export const lazyGetAttachmentWellInitialValue = new LazyImport(
    lazyModule,
    m => m.getAttachmentWellInitialValue
);

export const lazyGetAttachmentWellForCompose = new LazyImport(
    lazyModule,
    m => m.getAttachmentWellForCompose
);

export const lazyGetConversationAttachmentWellInitialValue = new LazyImport(
    lazyModule,
    m => m.getConversationAttachmentWellInitialValue
);

export const lazyGetImageDimensionsFromFile = new LazyImport(
    lazyModule,
    m => m.getImageDimensionsFromFile
);

export const lazyGetMaxClassicAttachmentsSize = new LazyImport(
    lazyModule,
    m => m.getMaxClassicAttachmentsSize
);

export const lazyGetAvailableLocalAttachmentSize = new LazyImport(
    lazyModule,
    m => m.getAvailableLocalAttachmentSize
);

export const lazyCreateAttachmentFileFromExistingMailboxAttachment = new LazyImport(
    lazyModule,
    m => m.createAttachmentFileFromExistingMailboxAttachment
);

export const lazyMergeToConversationAttachmentWell = new LazyAction(
    lazyModule,
    m => m.mergeToConversationAttachmentWell
);

export const lazySortConversationAttachmentWell = new LazyAction(
    lazyModule,
    m => m.sortConversationAttachmentWell
);

export const lazyConvertImageViewAttachmentToInlineAttachment = new LazyAction(
    lazyModule,
    m => m.convertImageViewAttachmentToInlineAttachment
);

export const lazyGetLastCloudyAttachmentsSharingIssueForBlockOnSend = new LazyImport(
    lazyModule,
    m => m.getLastCloudyAttachmentsSharingIssueForBlockOnSend
);

export const lazyGetAllValidAttachments = new LazyImport(lazyModule, m => m.getAllValidAttachments);

// NOTE: These are exported so that clients using attachment well
// can create orchestrators to react to various actions.
// Satchel 3 actions
export { onAttachmentCreated, onAttachmentDeleted };

// Types
export {
    AttachmentDataProviderItem,
    AttachmentDataProviderItemType,
    AttachmentFile,
    AttachmentFileList,
    AttachmentFullViewState,
    AttachmentFullViewStrategy,
    AttachmentId,
    AttachmentMenuAction,
    AttachmentModelPropertyValues,
    AttachmentPreviewMethod,
    AttachmentState,
    AttachmentStateType,
    AttachmentType,
    AttachmentViewState,
    AttachmentWellViewState,
    ClientAttachmentId,
    ClientItemId,
    CloudFile,
    CloudFileList,
    ConversationAttachmentWellViewState,
    CreateAttachmentDependencies,
    CreateAttachmentProcessResult,
    CreateAttachmentsProperties,
    ImageLoadState,
    InfoBarMessageViewState,
    InlineAttachmentState,
    InlineAttachmentStatus,
    ItemId,
    MailboxInfo,
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
    ShareAttachmentProperties,
    ShouldShareAttachment,
    WacAttachmentType,
};
